import { Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import L, { LatLngLiteral } from "leaflet";
import "leaflet/dist/leaflet.css";
import { PropsWithChildren, Suspense, useEffect, useMemo, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  AttributionControl,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
} from "react-leaflet";
import { StationLocationDto } from "use-smart-locks-shared";
import { routes } from "../../routes";
import { env } from "../../env";
import { getAppLink } from "../app-links";
import { getCampingStationBySlug } from "../api/nest-api";
import { useWatchGeolocation } from "../hooks/use-geolocation";
import { useQRCodeScanner } from "../hooks/use-qr-code-scanner";
import { useWatchStationLocations } from "../queries/use-station-locations-query";
import { IconBike, IconCar, IconQrCodeScanner } from "./icons";
import styles from "./station-map.module.css";

const MAX_VISIBLE_DISTANCE_METERS = 50_000;
const INITIAL_MAP_BOUNDS_METERS = MAX_VISIBLE_DISTANCE_METERS * 2;
const INITIAL_MAP_CENTER: LatLngLiteral = { lat: 48.5, lng: 11.5 };
const MAX_STATION_FOCUS_ZOOM = 13;
const CARTO_BASEMAP_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=" +
  env.cartoBasemapApiKey;

type StationMarkerIconProps = {
  name: string | null;
  showAvailability: boolean;
  bikeCount: number;
  carCount: number;
};

const StationMarkerIcon: React.FC<StationMarkerIconProps> = ({
  name,
  showAvailability,
  bikeCount,
  carCount,
}) => (
  <div className={styles.marker}>
    <span>{name ?? ""}</span>
    {showAvailability && (
      <div className={styles.markerCounts}>
        <span className={styles.markerCount}>
          <IconBike
            width={14}
            height={14}
            className={styles.markerCountValueBike}
          />
          <span className={styles.markerCountValueBike}>{bikeCount}</span>
        </span>
        <span className={styles.markerCount}>
          <IconCar
            width={14}
            height={14}
            className={styles.markerCountValueCar}
          />
          <span className={styles.markerCountValueCar}>{carCount}</span>
        </span>
      </div>
    )}
  </div>
);

const makeStationIcon = (
  name: string | null,
  showAvailability: boolean,
  bikeCount: number,
  carCount: number,
) =>
  L.divIcon({
    className: "", // Clears Leaflet's default white background and border.
    html: renderToStaticMarkup(
      <StationMarkerIcon
        name={name}
        showAvailability={showAvailability}
        bikeCount={bikeCount}
        carCount={carCount}
      />,
    ),
    // Allows variable-width pills; their CSS transform handles centering.
    iconSize: [0, 0],
    iconAnchor: [0, 0], // Positioning is handled by the CSS transform above.
  });

const userLocationIcon = L.divIcon({
  className: "",
  html: `<div class="${styles.userLocationRoot}"><div class="${styles.userLocationPulse}" aria-hidden="true"></div><div class="${styles.userLocationDot}"></div></div>`,
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

const UserLocationDot: React.FC<{ lat: number; lng: number }> = ({
  lat,
  lng,
}) => {
  const map = useMap();
  const didInitialFitRef = useRef(false);

  useEffect(() => {
    if (didInitialFitRef.current) return;
    map.fitBounds(L.latLng(lat, lng).toBounds(INITIAL_MAP_BOUNDS_METERS));
    didInitialFitRef.current = true;
  }, [lat, lng, map]);

  return (
    <Marker position={[lat, lng]} icon={userLocationIcon} interactive={false} />
  );
};

const MapOverlay: React.FC<PropsWithChildren> = ({ children }) => (
  <div className={styles.geolocationOverlay}>
    <Flex
      direction="column"
      align="center"
      gap="3"
      p="5"
      className={styles.geolocationOverlayContent}
    >
      {children}
    </Flex>
  </div>
);

const GeolocationLoadingOverlay: React.FC = () => (
  <MapOverlay>
    <Spinner size="3" />
    <Text size="2" color="gray">
      Standort wird ermittelt...
    </Text>
  </MapOverlay>
);

const GeolocationErrorOverlay: React.FC<{
  message: string;
  onRetry: () => void;
}> = ({ message, onRetry }) => (
  <MapOverlay>
    <Text size="4" weight="bold">
      Standort nicht verfügbar
    </Text>
    <Text size="2" align="center" color="gray">
      {message}
    </Text>
    <Button onClick={onRetry} size="2">
      Erneut versuchen
    </Button>
  </MapOverlay>
);

const EmbeddedMapMessage: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <MapOverlay>
    <Text size="4" weight="bold" align="center">
      {title}
    </Text>
    <Text size="2" align="center" color="gray">
      {description}
    </Text>
  </MapOverlay>
);

const StationMarkerLayer: React.FC<{ locations: StationLocationDto[] }> = ({
  locations,
}) => {
  const markers = useMemo(
    () =>
      locations.map((location) => ({
        id: location.id,
        position: {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
        } satisfies LatLngLiteral,
        icon: makeStationIcon(
          location.name,
          Boolean(location.showAvailability),
          location.bikeCount,
          location.carCount,
        ),
      })),
    [locations],
  );

  return markers.map((marker) => (
    <Marker key={marker.id} position={marker.position} icon={marker.icon} />
  ));
};

type NearbyStationMarkersProps = {
  userLatitude: number | null;
  userLongitude: number | null;
};

const NearbyStationMarkers: React.FC<NearbyStationMarkersProps> = ({
  userLatitude,
  userLongitude,
}) => {
  const { data: locations } = useWatchStationLocations();
  const { scanQRCode, isLoading } = useQRCodeScanner();

  const visibleLocations = useMemo<StationLocationDto[]>(() => {
    if (userLatitude === null || userLongitude === null) {
      return [];
    }

    const userPosition = L.latLng(userLatitude, userLongitude);

    return locations.filter((location) => {
      const stationPosition = L.latLng(
        parseFloat(location.latitude),
        parseFloat(location.longitude),
      );

      return (
        userPosition.distanceTo(stationPosition) <= MAX_VISIBLE_DISTANCE_METERS
      );
    });
  }, [locations, userLatitude, userLongitude]);

  return (
    <>
      <StationMarkerLayer locations={visibleLocations} />
      {userLatitude !== null &&
        userLongitude !== null &&
        visibleLocations.length === 0 && (
          <MapOverlay>
            <Flex
              direction="column"
              align="center"
              gap="2"
              p="5"
              className={styles.noStationsOverlayContent}
            >
              <Text size="3" weight="medium" align="center">
                Scannen Sie den QR-Code der Einheit.
              </Text>

              <Button
                variant="solid"
                highContrast
                color="gray"
                loading={isLoading}
                onClick={() => void scanQRCode()}
              >
                <IconQrCodeScanner width="18" height="18" />
                Scannen
              </Button>
            </Flex>
          </MapOverlay>
        )}
    </>
  );
};

const FitStationLocations: React.FC<{
  locations: StationLocationDto[];
  scope: string;
}> = ({ locations, scope }) => {
  const map = useMap();
  const fittedScopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (locations.length === 0 || fittedScopeRef.current === scope) return;

    const bounds = L.latLngBounds(
      locations.map((location) => [
        parseFloat(location.latitude),
        parseFloat(location.longitude),
      ]),
    );

    map.fitBounds(bounds, {
      animate: false,
      maxZoom: MAX_STATION_FOCUS_ZOOM,
      paddingTopLeft: [24, 24],
      paddingBottomRight: [24, 96],
    });
    fittedScopeRef.current = scope;
  }, [locations, map, scope]);

  return null;
};

const StationFocusedMarkers: React.FC<{
  stationSlug: string;
}> = ({ stationSlug }) => {
  const stationQuery = useQuery({
    queryKey: ["station", stationSlug],
    queryFn: () => getCampingStationBySlug(stationSlug),
    enabled: stationSlug.length > 0,
  });
  const { data: locations } = useWatchStationLocations();

  const stationNotFound =
    stationQuery.isError &&
    isAxiosError(stationQuery.error) &&
    stationQuery.error.response?.status === 404;

  if (stationQuery.isError && !stationNotFound) {
    throw stationQuery.error;
  }

  if (!stationSlug) {
    return (
      <EmbeddedMapMessage
        title="Region nicht verfügbar"
        description="Der Parameter „station“ darf nicht leer sein."
      />
    );
  }

  if (stationQuery.isPending) {
    return (
      <MapOverlay>
        <Spinner size="3" />
        <Text size="2" color="gray">
          Region wird geladen...
        </Text>
      </MapOverlay>
    );
  }

  if (stationNotFound) {
    return (
      <EmbeddedMapMessage
        title="Region nicht verfügbar"
        description={`Für „${stationSlug}“ wurde keine Region gefunden.`}
      />
    );
  }

  const visibleLocations = locations.filter(
    (location) => location.stationId === stationQuery.data?.id,
  );

  if (visibleLocations.length === 0) {
    return (
      <EmbeddedMapMessage
        title="Keine Stationen verfügbar"
        description="Für diese Region sind derzeit keine Stationen hinterlegt."
      />
    );
  }

  return (
    <>
      <FitStationLocations locations={visibleLocations} scope={stationSlug} />
      <StationMarkerLayer locations={visibleLocations} />
    </>
  );
};

const StationMapCanvas: React.FC<PropsWithChildren> = ({ children }) => (
  <MapContainer
    center={INITIAL_MAP_CENTER}
    zoom={10}
    zoomControl={false}
    attributionControl={false}
    style={{ height: "100%" }}
  >
    <TileLayer
      url={CARTO_BASEMAP_URL}
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
    />
    <AttributionControl prefix="" position="bottomright" />
    {/* Keep async map layers inside their own boundary to avoid Leaflet's
        "already initialized" error while query data is restored. */}
    <Suspense>{children}</Suspense>
  </MapContainer>
);

const EmbeddedBookingAction: React.FC = () => {
  const bookingLink = getAppLink(routes.appHandoff);

  return (
    <div className={styles.embeddedBookingAction}>
      <Button
        asChild
        size="4"
        highContrast
        color="gray"
        className={styles.embeddedBookingButton}
      >
        <a href={bookingLink} target="_top">
          Jetzt buchen
        </a>
      </Button>
    </div>
  );
};

const NearbyStationMap: React.FC<{ showBookingAction: boolean }> = ({
  showBookingAction,
}) => {
  const { latitude, longitude, error, isLoading, retry } =
    useWatchGeolocation();

  return (
    <div className={styles.mapRoot}>
      <StationMapCanvas>
        <NearbyStationMarkers
          userLatitude={latitude}
          userLongitude={longitude}
        />
        {latitude !== null && longitude !== null && (
          <UserLocationDot lat={latitude} lng={longitude} />
        )}
      </StationMapCanvas>
      {isLoading && <GeolocationLoadingOverlay />}
      {!isLoading && error !== null && (
        <GeolocationErrorOverlay message={error} onRetry={retry} />
      )}
      {showBookingAction && <EmbeddedBookingAction />}
    </div>
  );
};

const StationFocusedMap: React.FC<{
  showBookingAction: boolean;
  stationSlug: string;
}> = ({ showBookingAction, stationSlug }) => {
  return (
    <div className={styles.mapRoot}>
      <StationMapCanvas>
        <StationFocusedMarkers stationSlug={stationSlug} />
      </StationMapCanvas>
      {showBookingAction && <EmbeddedBookingAction />}
    </div>
  );
};

type StationMapProps = {
  embedded?: boolean;
  stationSlug: string | null;
};

export const StationMap: React.FC<StationMapProps> = ({
  embedded = false,
  stationSlug,
}) =>
  stationSlug !== null ? (
    <StationFocusedMap showBookingAction={embedded} stationSlug={stationSlug} />
  ) : (
    <NearbyStationMap showBookingAction={embedded} />
  );
